import React from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Stack,
  List,
  Skeleton,
  Alert,
  AlertTitle,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { TripMatch } from '@marageti/z4-sdk/lib/travel/TripMatch';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import ReminderCard from './reminder-card';
import AssignedToSelect from '../../components/assignee-input';

const RemindersLoading = () => (
  <Stack spacing={4}>
    <Box>
      <Typography variant="h3">
        Overdue
      </Typography>
      <List>
        <Stack spacing={1}>
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
        </Stack>
      </List>
    </Box>
    <Box>
      <Typography variant="h3">
        Today
      </Typography>
      <List>
        <Stack spacing={1}>
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width="100%" height={125} sx={{ borderRadius: 1 }} />
        </Stack>
      </List>
    </Box>
  </Stack>
);

const RemindersEmpty = () => (
  <Card sx={{ backgroundColor: 'common.success10' }}>
    <CardContent>
      <Stack spacing={1.25} alignItems="center">
        <CheckIcon color="success" sx={{ fontSize: 48 }} />
        <Typography variant="body1">
          There are currently no follow-up actions required.
        </Typography>
      </Stack>
    </CardContent>
  </Card>
);

const RemindersError = ({ error }: { error: ApiError }) => (
  <Alert severity="error" sx={{ py: 2 }}>
    <AlertTitle>An error occurred while retrieving data</AlertTitle>
    {`Code: ${error.code}: ${error.message}`}
    {error.id && `ID: ${error.id}`}
  </Alert>
);

const RemindersList = ({
  title,
  children,
}: {
  title: string,
  children: React.ReactNode,
}) => (
  <Box>
    <Typography variant="h3">
      {title}
    </Typography>
    <List>
      <Stack spacing={1}>
        {children}
      </Stack>
    </List>
  </Box>
);

const RemindersView = ({
  overdueReminders,
  dueTodayReminders,
  isLoading,
  error,
  selectedAssignee,
  isAdminViewer,
  handleAssigneeChange,
  dismissReminder,
  snoozeReminder,
}: {
  overdueReminders: TripMatch[],
  dueTodayReminders: TripMatch[],
  isLoading: boolean,
  error: ApiError,
  selectedAssignee: string,
  isAdminViewer: boolean,
  handleAssigneeChange: (assignee: string) => void,
  dismissReminder: (tripMatchId: string) => void,
  snoozeReminder: (tripMatchId: string, selectedDueAt: Date) => void,
}) => (
  <Container
    maxWidth="xl"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <Box
      sx={{
        width: '100%',
        maxWidth: {
          desktop: '774px',
        },
        py: { desktop: 8, mobile: 4 },
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" gutterBottom>
          Follow Up Reminders
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Stay on top of leads with reminders to message, call, and keep trip planning on track.
        </Typography>

        {isAdminViewer && (
          <Box sx={{ maxWidth: '280px', mt: 2 }}>
            <AssignedToSelect
              assignedTo={selectedAssignee}
              onChange={handleAssigneeChange}
              hideNotAssigned
            />
          </Box>
        )}
      </Box>
      <Stack spacing={4}>
        {isLoading && <RemindersLoading />}
        {error && <RemindersError error={error} />}
        {overdueReminders.length === 0 && dueTodayReminders.length === 0 && <RemindersEmpty />}

        {overdueReminders.length > 0 && (
          <RemindersList title={`Overdue (${overdueReminders.length})`}>
            {overdueReminders.map((tripMatch) => (
              <ReminderCard
                key={tripMatch.id}
                tripMatch={tripMatch}
                dismissReminder={dismissReminder}
                snoozeReminder={snoozeReminder}
                showAssignedToName={selectedAssignee === 'all'}
              />
            ))}
          </RemindersList>
        )}

        {dueTodayReminders.length > 0 && (
          <RemindersList title={`Today (${dueTodayReminders.length})`}>
            {dueTodayReminders.map((tripMatch) => (
              <ReminderCard
                key={tripMatch.id}
                tripMatch={tripMatch}
                dismissReminder={dismissReminder}
                snoozeReminder={snoozeReminder}
                showAssignedToName={selectedAssignee === 'all'}
              />
            ))}
          </RemindersList>
        )}
      </Stack>
    </Box>
  </Container>
);

export default RemindersView;
